export interface GridViewportSnapshot {
  sheetId: string;
  scrollLeft: number;
  scrollTop: number;
}

const GRID_SCROLLER_CLASSNAME = "dvn-scroller";

export const isGridScroller = (
  target: EventTarget | null,
): target is HTMLElement =>
  target instanceof HTMLElement &&
  target.classList.contains(GRID_SCROLLER_CLASSNAME);

const getGridScroller = (gridWrapper: HTMLElement | null) =>
  gridWrapper?.querySelector<HTMLElement>(`.${GRID_SCROLLER_CLASSNAME}`) ??
  null;

export const captureGridViewport = (
  sheetId: string,
  scroller: Pick<HTMLElement, "scrollLeft" | "scrollTop">,
): GridViewportSnapshot => ({
  sheetId,
  scrollLeft: scroller.scrollLeft,
  scrollTop: scroller.scrollTop,
});

export const restoreGridViewport = ({
  gridWrapper,
  activeSheetId,
  snapshot,
}: {
  gridWrapper: HTMLElement | null;
  activeSheetId: string;
  snapshot: GridViewportSnapshot | null;
}) => {
  if (!snapshot || snapshot.sheetId !== activeSheetId) return false;

  const scroller = getGridScroller(gridWrapper);
  if (!scroller) return false;

  const hasMoved =
    Math.abs(scroller.scrollLeft - snapshot.scrollLeft) > 1 ||
    Math.abs(scroller.scrollTop - snapshot.scrollTop) > 1;
  if (!hasMoved) return false;

  scroller.scrollLeft = snapshot.scrollLeft;
  scroller.scrollTop = snapshot.scrollTop;
  scroller.dispatchEvent(new Event("scroll"));
  return true;
};

const GRID_VIEWPORT_RESTORE_DELAYS_MS = [40, 120];

export const scheduleGridViewportRestore = (
  params: Parameters<typeof restoreGridViewport>[0],
) => {
  const restore = () => restoreGridViewport(params);
  restore();
  const animationFrame = requestAnimationFrame(restore);
  const timers = GRID_VIEWPORT_RESTORE_DELAYS_MS.map((delay) =>
    window.setTimeout(restore, delay),
  );

  return () => {
    cancelAnimationFrame(animationFrame);
    timers.forEach(window.clearTimeout);
  };
};

const GRID_SCROLL_REASSERT_DELAYS_MS = [40, 120, 220];

export const getGridRowScrollTarget = ({
  row,
  viewportHeight,
  scrollHeight,
  getRowHeight,
}: {
  row: number;
  viewportHeight: number;
  scrollHeight: number;
  getRowHeight: (row: number) => number;
}) => {
  let rowTop = 0;
  for (let index = 0; index < row; index += 1) {
    rowTop += getRowHeight(index);
  }

  const centeredTarget = rowTop + getRowHeight(row) / 2 - viewportHeight / 2;
  const maxScrollTop = Math.max(0, scrollHeight - viewportHeight);

  return Math.min(Math.max(0, centeredTarget), maxScrollTop);
};

export const scheduleGridRowScroll = ({
  gridWrapper,
  row,
  getRowHeight,
}: {
  gridWrapper: HTMLElement | null;
  row: number;
  getRowHeight: (row: number) => number;
}) => {
  const scrollToRow = () => {
    const scroller = getGridScroller(gridWrapper);
    if (!scroller) return;

    const target = getGridRowScrollTarget({
      row,
      viewportHeight: scroller.clientHeight,
      scrollHeight: scroller.scrollHeight,
      getRowHeight,
    });
    if (Math.abs(scroller.scrollTop - target) <= 2) return;

    scroller.scrollTop = target;
    scroller.dispatchEvent(new Event("scroll"));
  };

  const animationFrame = requestAnimationFrame(scrollToRow);
  const timers = GRID_SCROLL_REASSERT_DELAYS_MS.map((delay) =>
    window.setTimeout(scrollToRow, delay),
  );

  return () => {
    cancelAnimationFrame(animationFrame);
    timers.forEach(window.clearTimeout);
  };
};
